import { useSyncExternalStore, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { readEnv } from '@/env';
import {
  getMockIdentity,
  setMockIdentity,
  subscribeMockIdentity,
  type MockIdentity,
} from '@/auth/mock/mockIdentity';

/**
 * Dev-only header control to flip between the two mock identities (tenant /
 * platform admin) introduced for PTV2-1021. Returns `null` entirely (not just
 * CSS-hidden) unless `VITE_AUTH_MODE === 'mock'`, so real-auth/production builds
 * never carry an identity switch in the DOM. It is styled with an unmistakable
 * amber DEV badge so it reads as scaffolding, not a shippable feature. Reads and
 * writes the same module-level store + localStorage key (`ptv2-mock-identity`)
 * the provider uses.
 */
export function MockIdentitySwitcher(): ReactElement | null {
  const { t } = useTranslation();
  const identity = useSyncExternalStore(subscribeMockIdentity, getMockIdentity, getMockIdentity);

  let isMock = false;
  try {
    isMock = readEnv().authMode === 'mock';
  } catch {
    isMock = false;
  }
  if (!isMock) return null;

  return (
    <label
      data-testid="mock-identity-switcher"
      className="flex items-center gap-1.5 rounded-md border border-amber-400/70 bg-amber-400/15 px-1.5 py-0.5 text-[11px] text-amber-50"
    >
      <span className="rounded bg-amber-400 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-amber-950">
        {t('routes.platform.mockSwitcher.badge')}
      </span>
      <span className="sr-only">{t('routes.platform.mockSwitcher.label')}</span>
      <select
        aria-label={t('routes.platform.mockSwitcher.label')}
        value={identity}
        onChange={(e) => {
          setMockIdentity(e.target.value as MockIdentity);
        }}
        className="rounded border border-amber-300/50 bg-amber-950/30 px-1 py-0.5 text-amber-50"
      >
        <option className="text-foreground" value="tenant">
          {t('routes.platform.mockSwitcher.tenant')}
        </option>
        <option className="text-foreground" value="platform">
          {t('routes.platform.mockSwitcher.platform')}
        </option>
      </select>
    </label>
  );
}
